import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Payout } from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Table, useAlert } from '@frontend/design-system';
import { usePayoutShallowStore } from '../../../hooks/payouts-provider';
import { useColumnsWithPayoutDescriptor } from '../../../hooks/table/use-columns-with-payout-descriptor';
import { useListPayoutsV1InfiniteQuery } from '../../../hooks/use-payouts-list';
import { PaymentsTableInstances } from '../../../utils/helpers';
import { generateColumns } from './generate-columns';

export const PayoutsList = () => {
  const alerts = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation('payments');
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { numRows, setNumRows, stringifiedFilter, currentPageIndex, setCurrentPageIndex } = usePayoutShallowStore(
    'numRows',
    'setNumRows',
    'stringifiedFilter',
    'currentPageIndex',
    'setCurrentPageIndex'
  );

  const { columns } = useColumnsWithPayoutDescriptor<Payout>({
    forPrint: false,
    hideLocationNameColumn: locationIds.length === 1,
    generateColumns,
    tableName: PaymentsTableInstances.Payouts,
  });

  const rowsPerPageOptions = [10, 25, 50, 75, 100];

  const {
    data,
    fetchNextPage,
    hasNextPage, // Does the server have more pages?
    isFetchingNextPage, // Is a fetchNextPage call in progress?
    isLoading,
  } = useListPayoutsV1InfiniteQuery({
    requestParams: {
      locationIds,
      pageSize: numRows,
      filter: stringifiedFilter,
    },
  });

  // Derived data:
  // The actual list of payouts for the current page index we want to display
  const currentPayoutsToDisplay = useMemo(
    () => data?.pages[currentPageIndex]?.payouts || [],
    [data, currentPageIndex, numRows, stringifiedFilter]
  );

  // Total number of pages fetched so far
  const numberOfFetchedPages = data?.pages?.length || 0;

  /**
   * Navigates to the previously fetched page if one exists.
   */
  const goToPreviousPage = () => {
    // Can only go to previous if not on the first page (index 0)
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  /**
   * Navigates to the next page.
   * If the next page's data is already fetched, it displays it.
   * If the next page's data is not fetched but available on the server,
   * it triggers a fetch for that page and then displays it.
   */
  const goToNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    if (nextPageIndex < numberOfFetchedPages) {
      // The next page's data is already fetched.
      // Simply update the display index to show it.
      setCurrentPageIndex(nextPageIndex);
    } else if (nextPageIndex === numberOfFetchedPages && hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
      setCurrentPageIndex(nextPageIndex);
    }
  };

  const pageIndexAlreadyFetched = currentPageIndex < numberOfFetchedPages - 1;
  const onLastFetchedPage = currentPageIndex === numberOfFetchedPages - 1;

  const canGoPrevious = currentPageIndex > 0;
  const canGoNext = pageIndexAlreadyFetched || (onLastFetchedPage && !!hasNextPage);

  return (
    <Table
      tableStyle={css`
        min-height: 500px;
        height: 100%;
      `}
      styleConfig={{
        columns: [
          {
            id: '*',
            cellStyler: css`
              padding: ${theme.spacing(1, 1.5)};
            `,
            headerStyler: css`
              padding: ${theme.spacing(2, 1.5)};
            `,
          },
        ],
      }}
      globalTrackingId='pay-portal-payouts-PayoutsAPI-V1'
      tableInstanceId={PaymentsTableInstances.Payouts}
      colConfig={columns}
      data={currentPayoutsToDisplay}
      isLoading={isLoading}
      loadingRowsCount={numRows}
      manualSortBy
      emptyStateConfig={{
        type: 'payments',
        header: t('No data to display'),
      }}
      isPaginated
      manualPaginationConfig={{
        page: currentPageIndex + 1,
        hasNext: canGoNext,
        hasPrevious: canGoPrevious,
        onNumRowsChange: (num) => setNumRows(num),
        defaultRowsPerPage: numRows,
        rowsPerPageOptions,
        handleChange: (prevOrNext: 'prev' | 'next') => {
          if (prevOrNext === 'prev' && canGoPrevious) {
            goToPreviousPage();
          } else if (prevOrNext === 'next' && canGoNext) {
            goToNextPage();
          }
        },
      }}
      rowActions={{
        onRowClick: (payout: Payout) => {
          if (!payout.payoutId || !payout.locationId) {
            alerts.error('Unable to find payout details. Please try again.');
            return;
          }
          return navigate({
            to: '/payments/payouts/$payoutId/$locationId',
            params: { payoutId: payout.payoutId, locationId: payout.locationId },
          });
        },
      }}
    />
  );
};
